

import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Button,
} from "@mui/material";


import { TipReportPage } from "./tipReport";
import { ClockInReportPage } from "./ClockInReportPage";
import { EmployeeReportPage } from "./EmployeeReportPage";
import { SalesReportPage } from "./SalesReportPage";
import { OrderReportPage } from "./OrderReportPage";
import { ItemSalesReportPage } from "./ItemSalesReportPage"

// --- End Path Adjustments ---

export const ReportWrapper = () => {
  const [reportType, setReportType] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const reportRef = useRef();

  useEffect(() => {
    setIsLoading(true);
    const path = window.location.pathname;
    let type = null;
    let storageKey = null;

    console.log("ReportWrapper: Current path:", path); // Log path

    // Determine report type and storage key based on URL path
    if (path.includes("tip")) {
      type = "tip";
      storageKey = "tipReportData";
    } else if (path.includes("clock-in")) {
      type = "clock-in";
      storageKey = "clockInReportData";
    } else if (path.includes("epr")) {
      type = "employee";
      storageKey = "employeeReportData";
    } else if (path.includes("sales-report")) {
      type = "sales";
      storageKey = "salesReportData";
    } else if (path.includes("orders-report")) {
      type = "orders";
      storageKey = "orderReportData";
    } else if (path.includes("item-report")) {
      type = "item";
      storageKey = "itemSaleReportData";
    }

    console.log(`ReportWrapper: Determined type: ${type}, storageKey: ${storageKey}`); // Log determined type/key

    if (type && storageKey) { // Check if both type and key were found
      setReportType(type);
      const storedData = sessionStorage.getItem(storageKey);
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);

          // Check if rawData is an array OR if it's the employee report (special case)
          const dataPayload = (type === 'employee' && !parsedData.rawData) ? parsedData : parsedData.rawData;
          const isValidData = parsedData;
          if (isValidData) {
            setReportData(parsedData); // Set the whole parsed object
          } else {

            setReportData(null); // Set to null if data structure is wrong
          }
        } catch (error) {

          setReportData(null); // Clear data on parsing error
        }
      } else {

        setReportData(null); // Clear data if nothing found
      }
    } else {

      setReportData(null); // Clear data if type/key is unknown
    }
    setIsLoading(false); // Finish loading
  }, []); // Empty dependency array ensures this runs only once on mount

  const handlePrint = () => {
    // ... (handlePrint logic remains the same) ...
    if (!reportRef.current) {

      return;
    }
    const printContents = reportRef.current.innerHTML;
    const printWindow = window.open("", "_blank", "width=900,height=700");
    if (!printWindow) {

      return;
    }
    printWindow.document.write(`<html><head><title>${reportType?.toUpperCase()} REPORT</title><style>body { font-family: Arial, sans-serif; margin: 20px; background: #fff; } h5, h6, p, div, span, td, th { font-size: 10pt; } h5 { text-align: center; margin-bottom: 10px; font-size: 14pt; } h6 { margin-bottom: 5px; font-size: 11pt; } table { border-collapse: collapse; width: 100%; margin-bottom: 15px; page-break-inside: auto; } tr { page-break-inside: avoid; page-break-after: auto; } th, td { border: 1px solid #ccc; padding: 4px; text-align: left; } th { background-color: #f2f2f2; font-weight: bold; } .recharts-wrapper { margin: 20px auto; display: block; max-width: 100%; } @media print { body { margin: 10mm; } .no-print { display: none !important; } .recharts-responsive-container, table, div { page-break-inside: avoid; } }</style></head><body>${printContents}</body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => { try { printWindow.print(); } catch (e) { console.error("Printing failed:", e); } }, 500);
  };


  if (isLoading) // Use isLoading state
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading report...</Typography>
      </Box>
    );

  // Check if reportData is missing AFTER loading
  const displayData = reportType === 'employee' ? reportData : reportData?.rawData;

  if (!reportData || !displayData) // Check if reportData exists AND the relevant data property exists
    return (
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh", p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">Error: Report data is missing or invalid.</Typography>
        <Typography sx={{ mt: 1 }}>Could not load data from the previous tab (sessionStorage). Please close this tab and try generating the report again.</Typography>
      </Box>
    );

  const Header = () => (
    <Box sx={{ mb: 2, textAlign: "center" }}>
      <Typography variant="h5" fontWeight="bold">
        {reportType === "tip" ? "Tip Report"
          : reportType === "clock-in" ? "Clock-In Report"
            : reportType === "sales" ? "Sales Report"
              : reportType === "item" ? "Items Sale Report"
                : reportType === "orders" ? "Order Report" // Correct title check
                  : reportType === "employee" ? `Employee Report${reportData.employeeName ? ` - ${reportData.employeeName}` : ''}`
                    : "Report"}
      </Typography>
      {reportData?.storeID && (
        <Typography variant="body2" color="text.secondary">
          Store ID: {reportData.storeID}
        </Typography>
      )}
      {reportData.startDate && reportData.endDate && !isNaN(new Date(reportData.startDate)) && !isNaN(new Date(reportData.endDate)) && (
        <Typography variant="body2">
          {new Date(reportData.startDate).toLocaleDateString()} — {new Date(reportData.endDate).toLocaleDateString()}
        </Typography>
      )}
      <Divider sx={{ my: 1 }} />
    </Box>
  );

  return (
    <Box
      sx={{
        maxWidth: "950px",
        mx: "auto",
        my: 4,
        p: { xs: 2, sm: 3 },
        backgroundColor: "#fff",
        boxShadow: 3,
        borderRadius: 2,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }} className="no-print">
        <Button variant="contained" color="primary" onClick={handlePrint}>
          Print / Save PDF
        </Button>
      </Box>

      <div ref={reportRef}>
        <Header />

        {/* Conditionally render the correct report component, passing the relevant data */}
        {reportType === "tip" && <TipReportPage rawData={reportData.rawData} />}
        {reportType === "clock-in" && (
          <ClockInReportPage rawData={reportData.rawData} />
        )}
        {/* Employee report uses data directly from reportData because it might be filtered */}
        {reportType === "employee" && (
          <EmployeeReportPage rawData={reportData.rawData || reportData} /> // Pass rawData if exists, else reportData itself
        )}
        {reportType === "sales" && (
          <SalesReportPage rawData={reportData.rawData} />
        )}
        {reportType === "orders" && ( // Correct type check
          <OrderReportPage rawData={reportData.rawData} />
        )}
        {reportType === "item" && ( // Correct type check
          <ItemSalesReportPage rawData={reportData.rawData} />
        )}
      </div>
    </Box>
  );
};

